'use client'

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Select from 'react-select'
import { useRouter } from 'next/navigation'

import {
  categoryAliasId,
  categoryIdUrlAlias,
  categoryList,
  categoryTranslations,
  typeAlias
} from '../helpers/utils'
import { APIClient } from '../helpers/api_helper'
import InputFactory from '../components/Estate/InputFactory'
import { MultipleSelectModal } from '../components/PrettySelect/MultipleSelectModal'
import { isMobile } from 'react-device-detect'
import { useTranslations } from 'next-intl'
import { Col, Form } from 'reactstrap'
import { useLocale } from 'use-intl'

const HomeSearchParametersComponent = ({
  selectedCategory,
  setSelectedCategory,
  selectedType,
  setSelectedType
}: {
  selectedCategory: any
  setSelectedCategory: any
  selectedType: any
  setSelectedType: any
}) => {
  const t = useTranslations('')
  const selectedLang = useLocale()

  const api = useMemo(() => new APIClient(), [])
  const headers = useMemo(
    () => ({ 'Accept-Language': selectedLang }),
    [selectedLang]
  )

  const [formData, setFormData]: any = useState({
    code: '',
    regions: [],
    parameters: {},
    multiSelectParameters: {}
  })

  const [parameters, setParameters] = useState<any>(null)
  const [initialData, setInitialData] = useState<any>({
    regions: []
  })
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [loadingButton, setLoadingButton] = useState<boolean>(false)
  const [showMore, setShowMore] = useState<boolean>(true)
  const [mobileShowParameters]: any = useState({
    6: { 1: [38], 2: [] },
    7: { 1: [38, 11, 37], 2: [11, 37] },
    8: { 1: [38], 2: [] },
    9: { 1: [38, 11, 15, 37], 2: [15, 37] }
  })

  const router = useRouter()

  const fetchParameters = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await api.get('/site/parameter/', {}, headers)
      setParameters(response)
    } catch (error) {
      console.error('Failed to fetch parameters:', error)
    } finally {
      setIsLoading(false)
    }
  }, [api, headers])

  const fetchInitialData = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await api.get('/site/initial-information/', {}, headers)

      setInitialData(transformObjectData(response))
    } catch (error) {
      console.error('Failed to fetch parameters:', error)
    } finally {
      setIsLoading(false)
    }
  }, [api, headers])

  useEffect(() => {
    fetchParameters()
    fetchInitialData()
  }, [fetchParameters])

  const transformObjectData = (data: any) => {
    const { regions, categories, additional_infos } = data

    const transformedRegionsData = regions.map((item: any) => ({
      value: item.id,
      label: item.translations[selectedLang].name
    }))
    const transformedCategoriesData = categories.map((item: any) => ({
      value: item.id,
      label: item.translations[selectedLang].name
    }))
    const transformedInfoData = additional_infos.map((item: any) => ({
      value: item.id,
      label: item.translations[selectedLang].name
    }))

    return {
      additional_infos: transformedInfoData,
      regions: transformedRegionsData,
      categories: transformedCategoriesData
    }
  }

  const handleMultiSelectChange = (name: string, selectedOptions: any) => {
    if (name === 'regions') {
      setFormData({ ...formData, [name]: selectedOptions })
    } else {
      const params: any = formData.multiSelectParameters
      params[name] = selectedOptions.map((region: any) => region.value)

      setFormData({
        ...formData,
        multiSelectParameters: params
      })
    }
  }

  const handleParameterInputChange = (e: any) => {
    const { id, value } = e.target
    const currentParams: any = { ...formData.parameters }

    if (id.includes('min') || id.includes('max')) {
      const baseId = id.replace(/-(min|max)/, '')
      currentParams[baseId] = {
        ...currentParams[baseId],
        [id.includes('min') ? 'min' : 'max']: value || undefined
      }
    } else {
      currentParams[id] = value
    }

    setFormData({
      ...formData,
      parameters: currentParams
    })
  }

  const saveSelectedCategory = (newCategory: number) => (e: any) => {
    setSelectedCategory(newCategory)
  }

  const saveSelectedType = (newType: number) => (e: any) => {
    setSelectedType(newType)
  }

  const handleSubmit = (event: any) => {
    event.preventDefault()
    setLoadingButton(true)

    const formDataEntries: any = new FormData(event.target);
    const params: any = {};
    const multipleItems: any = ['regions', '8', '37', '11'];

    for (const [key, value] of formDataEntries) {
      if (value) {
        if (multipleItems.includes(key)) {
          if (!params[key]) {
            params[key] = [];
          }
          params[key].push(value);
        } else {
          params[key] = value;
        }
      }
    }

    const queryString = new URLSearchParams(params).toString();

    const baseUrl = `${categoryIdUrlAlias[selectedCategory]}-for-${typeAlias[selectedType].toLowerCase()}`;

    router.push(`/${baseUrl}?${queryString}`)
  }

  const handleSelectedItems = (name: string, selectedItems: any) => {
    if (name === 'regions') {
      setFormData({
        ...formData,
        [name]: selectedItems
      })
    }
  }

  return (
    <div className='category-tabs gray-background'>
      <div className='home-filter container-main'>
        <ul
          className='nav nav-tabs home-categories-container'
          id='myTab'
          role='tablist'
        >
          {categoryList.map((category: any, index: number) => (
            <li
              className={`category-switchers nav-item ${categoryAliasId[category] === selectedCategory ? 'active' : ''}`}
              role='presentation'
              key={index}
              onClick={saveSelectedCategory(categoryAliasId[category])}
            >
              <a
                className={`nav-link category-item-wrapper ${categoryAliasId[category] === selectedCategory ? 'active' : ''}`}
                id={`${category}-tab`}
                data-bs-toggle='tab'
                role='tab'
                aria-selected='false'
                data-alias={category}
              >
                <i className={`icon-${category}`}></i>
                <p
                  className={`homeSearchTabText ${categoryAliasId[category] === selectedCategory ? 'active' : ''}`}
                >
                  {t(categoryTranslations[category])}
                </p>
              </a>
            </li>
          ))}
        </ul>

        <div className='tab-content' id='myTabContent'>
          <div className='form-options'>
            <div className='form-check form-check-inline type-toggle active'>
              <a className='type-switchers'>
                <input
                  className='form-check-input'
                  type='radio'
                  name='filter'
                  onChange={saveSelectedType(1)}
                  value='selling'
                  checked={selectedType === 1}
                />
                <label className='form-check-label' htmlFor='selling'>
                  {t('Selling')}
                </label>
              </a>
            </div>
            <div className='form-check form-check-inline type-toggle'>
              <a className='type-switchers'>
                <input
                  className='form-check-input'
                  type='radio'
                  name='filter'
                  onChange={saveSelectedType(2)}
                  value='rent'
                  checked={selectedType === 2}
                />
                <label className='form-check-label' htmlFor='rent'>
                  {t('Rent')}
                </label>
              </a>
            </div>
          </div>

          <Form onSubmit={handleSubmit}>
            <div className='row home-params-container'>
              <div className='col-md-2 mb-16'>
                <input
                  type='text'
                  id='code'
                  name='code'
                  className='form-control'
                  value={formData.code}
                  onChange={e =>
                    setFormData({ ...formData, [e.target.id]: e.target.value })
                  }
                  placeholder={t('Search by code')}
                />
              </div>

              <div className='col-md-4 mb-16'>
                {!isMobile ? (
                  <Select
                    value={formData.regions}
                    onChange={options =>
                      handleMultiSelectChange('regions', options)
                    }
                    options={initialData.regions}
                    placeholder={t('Region')}
                    instanceId='regions-select'
                    name='regions'
                    isMulti
                    closeMenuOnSelect={false}
                    styles={{
                      control: base => ({
                        ...base,
                        borderRadius: '8px',
                        borderColor: '#e8e8e8'
                      })
                    }}
                  />
                ) : (
                  <MultipleSelectModal
                    options={initialData.regions}
                    name='regions'
                    placeholder={t('Region')}
                    onSelectionApply={(items: any) =>
                      setFormData({ ...formData, regions: items })
                    }
                  />
                )}
              </div>

              {isLoading ? (
                <p>{t('Loading')}...</p>
              ) : (
                parameters?.[selectedType]?.[selectedCategory]?.map(
                  (param: any, index: number) => {
                    let handleInputChange =
                      param.type === 'Selective' ||
                      param.type === 'Selective (Multiple)'
                        ? (selectedOptions: any) =>
                            handleMultiSelectChange(param.id, selectedOptions)
                        : (e: any) => handleParameterInputChange(e)

                    if (param.id === 11) {
                      handleInputChange = selectedOptions =>
                        handleMultiSelectChange(param.id, selectedOptions)
                    }

                    if (isMobile && showMore) {
                      if (
                        !mobileShowParameters[selectedCategory]?.[
                          selectedType
                        ]?.includes(param.id)
                      )
                        return null
                    }

                    const excludeIds = [56]
                    const rangeTypes = [
                      'Number',
                      'Decimal number',
                      'Comma formatted'
                    ]

                    let columnWidth = 3
                    if (rangeTypes.includes(param.type)) {
                      if (!excludeIds.includes(param.id) && param.id !== 11) {
                        columnWidth = 6
                      }
                    }

                    let value = formData.parameters[param.id]
                    const multiSelectParams = [8, 37, 11, 44, 35]

                    if (multiSelectParams.includes(param.id)) {
                      value = formData.multiSelectParameters[param.id]
                    }

                    return (
                      <Col md={columnWidth} key={index}>
                        <InputFactory
                          parameter={param}
                          value={value}
                          onChange={handleInputChange}
                          isRange={true}
                          isHomePage={true}
                          handleSelectedItems={handleSelectedItems}
                        />
                      </Col>
                    )
                  }
                )
              )}

              <p className="text-center show_more mobile" onClick={() => setShowMore(!showMore)}>
                {t('Show more')}
                {showMore ? <i className="fa fa-angle-down"></i> : <i className="fa fa-angle-up"></i>}
              </p>
            </div>

            <div className='d-flex justify-content-end'>
              <button
                type='submit'
                id='home-filters-submit'
                className='btn search-btn filters-submit border border-primary'
              >
                {loadingButton && <p>{t('Loading')}...</p>}
                {!loadingButton && t('Search') }
              </button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  )
}

export default HomeSearchParametersComponent
